// @greenlight-spec: 48-user-passthrough.md#per-user-oauth
// @greenlight-spec: 48-user-passthrough.md#the-callback-proves-the-same-user
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Unplug } from 'lucide-react';

import { Eyebrow } from '@/components/greenlight/Eyebrow';
import { Logo } from '@/components/greenlight/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';
import { SignInTestResultPage } from '@/pages/SignInTestResultPage';

type Outcome = 'declined' | 'refused' | 'failed';

/** Why the callback refused the connected account, in words the person can act on. */
const REFUSALS: Record<string, string> = {
  tenant_mismatch: 'That account belongs to a different Microsoft tenant than your organization’s.',
  oid_mismatch: 'That Microsoft account is not the one you signed in to Greenlight with.',
  oid_unrecorded:
    'Greenlight has not yet recorded which Microsoft account is yours. Sign out of Greenlight, sign in again, and retry.',
  profile_scope_missing:
    'Your organization’s Greenlight sign-in does not request the profile scope, so Greenlight cannot confirm which Microsoft account is yours. Ask your administrator to add profile to the sign-in scopes.',
  username_mismatch: 'That account’s username does not match your Greenlight email.',
  username_missing: 'The upstream did not say which account was connected.',
  email_mismatch: 'That account’s email does not match your Greenlight email.',
  email_unverified: 'The upstream has not verified that account’s email address.',
  email_missing: 'The upstream did not return an email for the connected account.',
  id_token_missing: 'The upstream did not identify the connected account.',
  id_token_invalid: 'The upstream’s identity token could not be verified.',
  state_mismatch: 'This connect attempt was started from a different session.',
  user_inactive: 'Your Greenlight account was deactivated before the connection completed.',
};

/**
 * Where `/connect/callback` sends a browser whose connect did not end in a
 * stored connection: the person declined consent, the connected account was
 * not theirs, or the upstream failed. Rendered outside the AppShell, like the
 * sign-in surfaces, because the person may be mid-redirect into an app.
 *
 * The page is public, so nothing from the query string becomes a link as-is:
 * the retry link is rebuilt from the integration id and credential slug, and
 * "back to the app" goes through `/connect/return`, where the server confines
 * the destination to the install's own hosts.
 */
export function ConnectResultPage(): React.JSX.Element {
  const [params] = useSearchParams();
  return params.get('test') === '1' ? <SignInTestResultPage /> : <ConnectResult />;
}

function ConnectResult(): React.JSX.Element {
  const [params] = useSearchParams();
  const outcome = (params.get('outcome') ?? 'failed') as Outcome;
  const integration = params.get('integration');
  const integrationId = params.get('integration_id');
  const credential = params.get('credential');
  const account = params.get('account');
  const reason = params.get('reason');
  const code = params.get('code');
  const returnTo = params.get('return_to');
  const retry =
    integrationId && credential
      ? `/connect?integration=${encodeURIComponent(integrationId)}&credential=${encodeURIComponent(credential)}${returnTo ? `&return_to=${encodeURIComponent(returnTo)}` : ''}`
      : null;
  const back = returnTo ? `/connect/return?to=${encodeURIComponent(returnTo)}` : null;

  React.useEffect(() => {
    document.title = 'Connect your account · Greenlight';
    return () => {
      document.title = 'Greenlight';
    };
  }, []);

  const name = integration ?? 'this integration';
  const heading =
    outcome === 'declined'
      ? `${name} was not connected`
      : outcome === 'refused'
        ? 'That account is not yours'
        : `Connecting ${name} did not work`;
  const body =
    outcome === 'declined'
      ? `The app needs to act as you in ${name}, and that needs your permission. Nothing was connected; you can allow it now or come back later, and the rest of the app still works.`
      : outcome === 'refused'
        ? (REFUSALS[reason ?? ''] ??
          'The connected account could not be matched to you, so nothing was saved.')
        : (REFUSALS[reason ?? ''] ??
          `${name} did not complete the connection. Nothing was saved, and the rest of the app still works; try again, and tell IT if it keeps failing.`);
  // What IT can act on: the provider's own code when there is one, else the reason.
  const forIt =
    outcome === 'failed' && !(reason && REFUSALS[reason])
      ? `${name} answered: ${[...new Set([code, reason])].filter(Boolean).join(' ') || 'no code'}`
      : null;

  return (
    <div
      className="relative flex min-h-[100dvh] flex-col items-center justify-center overflow-x-hidden bg-background px-5 py-12"
      data-testid="connect-result"
      data-outcome={outcome}
    >
      <div
        aria-hidden
        className="pointer-events-none fixed -top-[200px] left-1/2 h-[600px] w-[900px] -translate-x-1/2 bg-[radial-gradient(circle,hsl(var(--signal)/0.14)_0%,transparent_62%)]"
      />
      <div className="fixed right-4 top-4 z-10">
        <ThemeToggle />
      </div>

      <main className="relative z-[1] flex w-full max-w-[408px] flex-col items-center">
        <div className="mb-7 flex flex-col items-center gap-2">
          <Logo className="h-7 text-foreground" />
          <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
            by Shift AI
          </div>
        </div>

        <div className="w-full rounded-gl-lg border border-border bg-card p-6 text-center shadow-xl sm:p-8">
          <div className="mx-auto mb-4 flex size-11 items-center justify-center rounded-full bg-muted">
            {outcome === 'declined' ? (
              <Unplug className="size-6 text-muted-foreground" aria-hidden />
            ) : (
              <AlertTriangle className="size-6 text-muted-foreground" aria-hidden />
            )}
          </div>
          <Eyebrow>Connect your account</Eyebrow>
          <h1 className="text-[22px] font-semibold leading-tight tracking-[-0.02em]">{heading}</h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
          {forIt && (
            <p className="mt-3 text-sm text-muted-foreground" data-testid="connect-for-it">
              For IT: the app can’t connect to {name}.{' '}
              <span className="font-mono text-[12.5px] text-foreground wrap-anywhere">{forIt}</span>
            </p>
          )}
          {outcome === 'refused' && account && (
            <p className="mt-3 text-sm text-muted-foreground">
              Connected account:{' '}
              <span className="font-mono text-[12.5px] text-foreground wrap-anywhere">
                {account}
              </span>
            </p>
          )}
          <div className="mt-6 flex flex-col gap-2">
            {retry && (
              <Button asChild className="h-11 w-full">
                <a href={retry}>{outcome === 'declined' ? 'Allow and connect' : 'Try again'}</a>
              </Button>
            )}
            <Button asChild variant="outline" className="h-11 w-full">
              {back ? <a href={back}>Back to the app</a> : <Link to="/">Open dashboard</Link>}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
